import type { ChangeEvent, FocusEvent, ReactNode } from 'react';
import { useCallback, useMemo, useState } from 'react';
import { useDebounce } from 'react-use';

import type { RangeValues } from '@sorare/core/src/atoms/inputs/RangeSlider';
import { RangeSlider as RangeSliderAtom } from '@sorare/core/src/atoms/inputs/RangeSlider';
import {
  IconWrapper,
  InputsLine,
  Separator,
  StyledInput,
  StyledLabel,
} from '@sorare/core/src/components/search/RangeSliderWithInputs';
import { clamp } from '@sorare/core/src/lib/math';

interface Props {
  initialValues: RangeValues;
  onChange?: (values: RangeValues) => void;
  min?: number;
  max?: number;
  algorithm?: {
    scale: (x: number) => number;
    unscale: (x: number) => number;
  };
  fromInput?: (value: number) => number;
  toInput?: (value: number) => string;
  icon?: ReactNode;
}

const DEFAULT_ALGORITHM = {
  scale: (x: number) => x,
  unscale: (x: number) => x,
};

const defaultFromInputFn = (value: number) => value;
const defaultToInputFn = (value: number) => value.toString();

export const RangeSlider = ({
  initialValues,
  min = 0,
  max = 0,
  algorithm = DEFAULT_ALGORITHM,
  fromInput = defaultFromInputFn,
  toInput = defaultToInputFn,
  onChange,
  icon,
}: Props) => {
  const [currentValues, setCurrentValues] = useState<RangeValues>({
    low: algorithm.unscale(initialValues.low),
    high: algorithm.unscale(initialValues.high),
  });

  const [inputValues, setInputValues] = useState<{ low: string; high: string }>(
    {
      low: toInput(algorithm.scale(currentValues.low)),
      high: toInput(algorithm.scale(currentValues.high)),
    }
  );

  const [editingInputs, setEditingInputs] = useState(false);

  const { sliderMin, sliderMax } = useMemo(() => {
    return {
      sliderMin: algorithm.unscale(min),
      sliderMax: algorithm.unscale(max),
    };
  }, [algorithm, max, min]);

  const refineValues = useCallback(() => {
    const newMin = Math.max(algorithm.scale(currentValues.low!), min!);
    const newMax = Math.min(algorithm.scale(currentValues.high!), max!);
    onChange?.({ low: newMin, high: newMax });
  }, [algorithm, currentValues, max, min, onChange]);

  const changeInputValues = (range: { low?: string; high?: string }) => {
    setInputValues(prev => ({ ...prev, ...range }));
  };

  const changeSliderValues = useCallback(
    (range: { low?: number; high?: number }, updateInputValues?: boolean) => {
      if (
        range.high !== undefined &&
        Math.round(range.high) === Math.round(sliderMax)
      ) {
        range.high = sliderMax;
      }

      const clampedLow =
        range.low !== undefined
          ? clamp(range.low, sliderMin, sliderMax)
          : undefined;
      const clampedHigh =
        range.high !== undefined
          ? clamp(range.high, sliderMin, sliderMax)
          : undefined;

      setCurrentValues(prev => ({
        ...prev,
        ...(clampedLow !== undefined && { low: clampedLow }),
        ...(clampedHigh !== undefined && { high: clampedHigh }),
      }));

      if (updateInputValues) {
        changeInputValues({
          ...(clampedLow !== undefined && {
            low: toInput(algorithm.scale(clampedLow)),
          }),
          ...(clampedHigh !== undefined && {
            high: toInput(algorithm.scale(clampedHigh)),
          }),
        });
      }
    },
    [algorithm, toInput, sliderMin, sliderMax]
  );

  useDebounce(
    () => {
      if (editingInputs) {
        refineValues();
      }
    },
    400,
    [inputValues]
  );

  const disabled = min === max;

  return (
    <div>
      <RangeSliderAtom
        rangeValues={currentValues}
        min={sliderMin}
        max={sliderMax}
        disabled={disabled}
        onChange={(range: RangeValues) => {
          changeSliderValues(
            {
              low: range.low,
              high: range.high,
            },
            true
          );
        }}
        onMouseUp={refineValues}
      />
      <InputsLine>
        <StyledLabel>
          {!!icon && <IconWrapper>{icon}</IconWrapper>}
          <StyledInput
            id="a-input1"
            type="number"
            min={toInput(algorithm.scale(sliderMin))}
            max={toInput(algorithm.scale(sliderMax))}
            value={inputValues.low}
            onBlur={(event: FocusEvent<HTMLInputElement>) => {
              setEditingInputs(false);
              changeSliderValues(
                {
                  low: algorithm.unscale(fromInput(+event.target.value)),
                },
                true
              );
            }}
            onChange={(event: ChangeEvent<HTMLInputElement>) => {
              changeInputValues({
                low: event.target.value,
              });
              changeSliderValues({
                low: algorithm.unscale(fromInput(+event.target.value)),
              });
            }}
            onFocus={() => setEditingInputs(true)}
            withIcon={!!icon}
            borderColor="transparent"
          />
        </StyledLabel>
        <Separator>-</Separator>
        <StyledLabel>
          {!!icon && <IconWrapper>{icon}</IconWrapper>}
          <StyledInput
            id="b-input2"
            type="number"
            min={toInput(algorithm.scale(sliderMin))}
            max={toInput(algorithm.scale(sliderMax))}
            value={inputValues.high}
            onBlur={(event: FocusEvent<HTMLInputElement>) => {
              setEditingInputs(false);
              changeSliderValues(
                {
                  high: algorithm.unscale(fromInput(+event.target.value)),
                },
                true
              );
            }}
            onChange={(event: ChangeEvent<HTMLInputElement>) => {
              changeInputValues({
                high: event.target.value,
              });
              changeSliderValues({
                high: algorithm.unscale(fromInput(+event.target.value)),
              });
            }}
            onFocus={() => setEditingInputs(true)}
            withIcon={!!icon}
            borderColor="transparent"
          />
        </StyledLabel>
      </InputsLine>
    </div>
  );
};
